/**
 * @class
 * @function SlideShow
 */
if (!customElements.get('slide-show')) {
  class SlideShow extends HTMLElement {
    constructor() {
      super();

      const slideshow = this;
      const dataset = slideshow.dataset;

      // Configurable options
      const dots = dataset.dots === 'true';
      const slideshow_slides = Array.from(slideshow.querySelectorAll('.carousel__slide'));
      const autoplay = dataset.autoplay == 'false' ? false : parseInt(dataset.autoplay, 10);
      const align = dataset.align == 'center' ? 'center' : 'left';
      const fade = dataset.fade === 'true';
      const prev_button = slideshow.querySelector('.flickity-prev');
      const next_button = slideshow.querySelector('.flickity-next');
      const custom_dots = slideshow.querySelector('.flickity-page-dots');
      const progress_bar = slideshow.parentNode.querySelector('.flickity-progress--bar');
      const animations = [];
      const rightToLeft = document.dir === 'rtl';
      const animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';

      if (slideshow_slides.length < 1) return;

      // Base Flickity arguments
      const args = {
        wrapAround: true,
        cellAlign: align,
        pageDots: false,
        contain: true,
        fade: fade,
        autoPlay: autoplay,
        rightToLeft: rightToLeft,
        prevNextButtons: false,
        cellSelector: '.carousel__slide',
        on: {}
      };

      // Special slideshow types
      this.configureSpecialSlideshows(slideshow, args);

      // Progress bar
      if (progress_bar) {
        args.wrapAround = false;
        args.on.scroll = function(progress) {
          progress = Math.max(0, Math.min(1, progress));
          progress_bar.style.width = progress * 100 + '%';
        };
      }

      // Initialize Flickity
      const flkty = new Flickity(slideshow, args);
      slideshow.dataset.initiated = true;

      // Navigation buttons
      if (prev_button && next_button) {
        prev_button.addEventListener('click', () => flkty.previous());
        prev_button.addEventListener('keyup', () => flkty.previous());
        next_button.addEventListener('click', () => flkty.next());
        next_button.addEventListener('keyup', () => flkty.next());
      }

      // Shopify design mode support
      if (Shopify.designMode) {
        slideshow.addEventListener('shopify:block:select', (event) => {
          const index = slideshow_slides.indexOf(event.target);
          flkty.select(index);
        });
      }
    }

    // Configure specific slideshow types
    configureSpecialSlideshows(slideshow, args) {
      const animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';
      const dots = slideshow.dataset.dots === 'true';
      const custom_dots = slideshow.querySelector('.flickity-page-dots');

      if (slideshow.classList.contains('image-with-text-slideshow__image')) {
        const main_slideshow = slideshow.parentNode.querySelector('.image-with-text-slideshow__content');
        const image_slideshow_slides = slideshow.querySelectorAll('.image-with-text-slideshow__image-media');
        args.draggable = false;
        args.asNavFor = main_slideshow;
        if (image_slideshow_slides.length && image_slideshow_slides[0].classList.contains('desktop-height-auto')) {
          args.adaptiveHeight = true;
        }
      }

      if (slideshow.classList.contains('customer-reviews__image')) {
        const main_slideshow = slideshow.parentNode.querySelector('.customer-reviews__content');
        args.draggable = false;
        args.asNavFor = main_slideshow;
      }

      if (slideshow.classList.contains('image-with-text-slideshow__content') ||
          slideshow.classList.contains('testimonials__carousel') ||
          slideshow.classList.contains('customer-reviews__content') ||
          slideshow.classList.contains('customer-reviews__image')) {
        args.adaptiveHeight = true;
      }

      if (slideshow.classList.contains('custom-dots')) {
        if (animations_enabled && slideshow.classList.contains('main-slideshow')) {
          this.prepareAnimations(slideshow, []);
        }

        args.pauseAutoPlayOnHover = false;
        args.on = {
          staticClick: () => args.unpausePlayer(),
          ready: () => this.handleReady(slideshow, dots, custom_dots),
          change: (index) => this.handleChange(slideshow, index, dots, custom_dots)
        };
      }

      if (slideshow.classList.contains('shoppable-video-reels--carousel')) {
        args.on.staticClick = (event, pointer, cellElement, cellIndex) => {
          if (typeof cellIndex === 'number') {
            flkty.select(cellIndex);
          }
        };
      }

      if (slideshow.classList.contains('main-slideshow')) {
        if (slideshow.classList.contains('desktop-height-image') || slideshow.classList.contains('mobile-height-image')) {
          args.adaptiveHeight = true;
        }
      }

      if (slideshow.classList.contains('products')) {
        args.wrapAround = false;
        args.on.ready = function() {
          const flickity = this;
          const prev_button = slideshow.querySelector('.flickity-prev');
          const next_button = slideshow.querySelector('.flickity-next');
          if (next_button) {
            window.addEventListener('resize', () => slideshow.centerArrows(flickity, prev_button, next_button));
          }
          window.dispatchEvent(new Event('resize'));
        };
      }
    }

    // Video handling
    videoPause(video_container) {
      setTimeout(() => {
        if (!video_container) return;
        const provider = video_container.dataset.provider;
        if (provider === 'hosted') video_container.querySelector('video')?.pause();
        if (provider === 'youtube') video_container.querySelector('iframe')?.contentWindow.postMessage(JSON.stringify({ event: "command", func: "pauseVideo", args: "" }), "*");
        if (provider === 'vimeo') video_container.querySelector('iframe')?.contentWindow.postMessage(JSON.stringify({ method: "pause" }), "*");
      }, 10);
    }

    videoPlay(video_container) {
      setTimeout(() => {
        if (!video_container) return;
        const provider = video_container.dataset.provider;
        if (provider === 'hosted') video_container.querySelector('video')?.play();
        if (provider === 'youtube') video_container.querySelector('iframe')?.contentWindow.postMessage(JSON.stringify({ event: "command", func: "playVideo", args: "" }), "*");
        if (provider === 'vimeo') video_container.querySelector('iframe')?.contentWindow.postMessage(JSON.stringify({ method: "play" }), "*");
      }, 10);
    }

    // Animations
    prepareAnimations(slideshow, animations) {
      if (slideshow.dataset.animationsReady) return;

      new SplitText(slideshow.querySelectorAll('.split-text'), { type: 'lines, words', linesClass: 'line-child' });

      slideshow.querySelectorAll('.slideshow__slide').forEach((item, i) => {
        const tl = gsap.timeline({ paused: true });
        let button_offset = 0;

        animations[i] = tl;

        if (slideshow.dataset.transition === 'swipe') {
          tl.fromTo(item, { clipPath: 'polygon(0 0, 0 0, 0 100%, 0 100%)' }, { duration: 0.7, clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%)" }, "start");
        }

        tl.to(item.querySelector('.slideshow__slide-bg'), { duration: 1.5, scale: 1 }, "start");

        if (item.querySelector('.subheading')) {
          tl.to(item.querySelector('.subheading'), { duration: 0.5, autoAlpha: 1 }, 0);
          button_offset += 0.5;
        }

        if (item.querySelector('.slideshow__slide-content--heading')) {
          const lines = item.querySelectorAll('.slideshow__slide-content--heading .line-child div');
          const h1_duration = 0.5 + ((lines.length - 1) * 0.05);
          tl.from(lines, { duration: h1_duration, yPercent: '100', stagger: 0.05 }, 0);
          button_offset += h1_duration;
        }

        if (item.querySelector('p:not(.subheading)')) {
          const p_lines = item.querySelectorAll('p:not(.subheading) .line-child div');
          const p_duration = 0.5 + ((p_lines.length - 1) * 0.02);
          tl.from(p_lines, { duration: p_duration, yPercent: '100', stagger: 0.02 }, 0);
          button_offset += p_duration;
        }

        if (item.querySelectorAll('.button')) {
          tl.fromTo(item.querySelectorAll('.button'), { y: '100%' }, { duration: 0.5, y: '0%', stagger: 0.1 }, button_offset * 0.2);
        }

        item.dataset.timeline = tl;
      });

      slideshow.dataset.animationsReady = true;
    }

    animateSlides(i, animations) {
      document.fonts.ready.then(() => animations[i].timeScale(1).restart());
    }

    animateReverse(i, animations) {
      animations[i].timeScale(3).reverse();
    }

    centerArrows(flickity, prev_button, next_button) {
      let first_cell = flickity.cells[0];
      let max_height = 0;
      let image_height = first_cell.element.querySelector('.product-card--featured-image')?.clientHeight || 0;

      flickity.cells.forEach((item) => {
        if (item.size.height > max_height) max_height = item.size.height;
      });

      if (max_height > image_height) {
        const difference = (max_height - image_height) / -2;
        prev_button.style.transform = `translateY(${difference}px)`;
        next_button.style.transform = `translateY(${difference}px)`;
      }
    }
  }

  customElements.define('slide-show', SlideShow);
}
